import { ZITI_DATA_SERVICE, ZitiDataService, GrowlerService } from 'ziti-console-lib';
import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    OnChanges,
    SimpleChanges,
    OnDestroy,
    Inject,
} from '@angular/core';
import { isEmpty, cloneDeep, unset } from 'lodash';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { IpsecShare } from '@netfoundry-ui/shared/model';
import { ApiService, LoggerService, AgentService, IpsecShareService, IpsecTunnelService } from '@netfoundry-ui/shared/services';
import { Subscription } from 'rxjs';
import { GrowlerData } from '@netfoundry-ui/shared/growler';
import { RxTxSample } from '@netfoundry-ui/ui/rx-tx-timeline-chart';

@Component({
    selector: 'app-ipsec-share-form',
    templateUrl: './ipsec-share-form.component.html',
    styleUrls: ['./ipsec-share-form.component.scss'],
    standalone: false
})
export class IpsecShareFormComponent implements OnInit, OnChanges, OnDestroy {
    @Input() isModal = false;
    @Input() formData: any = {};
    close: EventEmitter<any> = new EventEmitter<any>();

    isEditing = false;
    errMap: any = {};

    moreActions: any[] = [];
    protected isLoading = false;
    initData: any = {};
    @Output() dataChange: EventEmitter<any> = new EventEmitter<any>();
    _dataChange = false;
    basePath = '';
    previousRoute;
    protected usePreviousLocation = true;
    subscription: Subscription = new Subscription();
    @Input() entityId: string;
    baseHref: any;
    formInit = false;
    frontdoorId: string;
    agentList;
    agentError = false;
    agentErrorMessage;
    nameError = false;
    nameErrorMessage;
    targetError = false;
    targetErrorMessage;
    ipsecTunnelError = false;
    ipsecTunnelErrorMessage;
    ingressPortError = false;
    ingressPortErrorMessage;
    ipsecTunnelList;

    processing = false;
    metricSamples: RxTxSample[] = [];
    metricsLoading = false;

    constructor(
        private apiService: ApiService,
        public agentService: AgentService,
        private logger: LoggerService,
        public svc: IpsecShareService,
        private ipsecTunnelService: IpsecTunnelService,
        private growlerService: GrowlerService,
        @Inject(ZITI_DATA_SERVICE) protected zitiService: ZitiDataService,
        private location: Location,
        protected router?: Router,
        protected route?: ActivatedRoute,
    ) {
        this.previousRoute = router?.getCurrentNavigation()?.previousNavigation?.finalUrl?.toString();
        this.subscription.add(
            this.route?.params?.subscribe((params) => {
                const id = params['id'];
                if (isEmpty(id)) {
                    this.entityId = 'create';
                } else {
                    this.entityId = id;
                }
                if (this.frontdoorId) {
                    this.loadFormForRouteEntity();
                }
            })
        );
        this.subscription.add(
            router?.events.subscribe((event: any) => {
                if (!event?.snapshot?.routeConfig?.path) {
                    return;
                }
                const pathSegments = event.snapshot.routeConfig.path.split('/');
                this.basePath = pathSegments[0];
            })
        );
        this.initBaseHref();
    }

    ngOnInit(): void {
        this.subscription.add(
            this.apiService.currentTenant.subscribe((tenant) => {
                this.agentService.getAccountById(tenant.id).subscribe((frontdoor: any) => {
                    this.frontdoorId = frontdoor.id;
                    this.loadFormForRouteEntity();
                });
            })
        );
        this.formInit = true;
    }

    /**
     * Loads create vs edit state from route `entityId` once `frontdoorId` exists.
     * Also re-runs when :id changes (same component instance).
     */
    private loadFormForRouteEntity(): void {
        if (!this.frontdoorId) {
            return;
        }
        if (!this.entityId || this.entityId === 'create') {
            this.isEditing = false;
            this.isLoading = false;
            this.formData = new IpsecShare();
            this.initData = cloneDeep(this.formData);
            this.entityUpdated();
            this.getAgents();
            this.getIpsecTunnels();
            return;
        }
        this.isEditing = true;
        this.isLoading = true;
        this.getIpsecShare();
    }

    getIpsecShare() {
        return this.svc.getIpsecShare(this.frontdoorId, this.entityId).subscribe({
            next: (entity: any) => {
                this.formData = entity;
                this.initData = cloneDeep(this.formData);
                this.entityUpdated();
                this.getIpsecTunnels();
                this.ensureSelectOptionsPatchedForCurrentShare();
                this.getAgents();
                this.isLoading = false;
                this.loadMetrics();
            },
            error: (err) => {
                this.growlerService.show(
                    new GrowlerData('error', 'Error', 'Error retrieving IPsec share data.')
                );
                this.isLoading = false;
            }
        });
    }

    ngOnChanges(_changes: SimpleChanges) {
        // Routed form: `isEditing` comes from `entityId` (see `loadFormForRouteEntity`). Do not derive it
        // from `formData.id` here — `formData` is filled asynchronously and is not a router @Input, so
        // syncing from `formData` can leave `isEditing` false on the edit page while the URL has an id.
        if (this.isModal) {
            this.isEditing = !isEmpty(this.formData?.id);
        }
    }

    initBaseHref() {
        try {
            const base = document?.querySelector('base');
            this.baseHref = base ? base.getAttribute('href') : '/';
        } catch (error) {
            this.baseHref = '/';
        }
    }

    entityUpdated() {
        unset(this.formData, '_links');
        this.initData = cloneDeep(this.formData);
    }

    headerActionRequested(action: any) {
        switch (action.name) {
            case 'save':
                this.save();
                break;
            case 'close':
                this.closeForm();
                break;
        }
    }

    getAgents() {
        this.agentService.getAgentsByEnv(this.frontdoorId, true).subscribe((agents: any) => {
            this.agentList = agents._embedded?.environmentList;
            this.ensureSelectOptionsPatchedForCurrentShare();
        });
    }

    /**
     * Ensures dropdown options include the share's current agent and tunnel so MatSelect can display
     * the selection (API/static demo data may use ids not yet present in the loaded option lists).
     */
    private ensureSelectOptionsPatchedForCurrentShare(): void {
        if (!this.isEditing || !this.formData) {
            return;
        }
        const zId = this.formData.envZId;
        if (zId) {
            const agents = [...(this.agentList ?? [])];
            if (!agents.some((a) => a?.zId === zId)) {
                agents.unshift({ zId, description: zId });
            }
            this.agentList = agents;
        }
        const tunnelId = this.formData.ipsecTunnelId;
        if (tunnelId) {
            const tunnels = [...(this.ipsecTunnelList ?? [])];
            if (!tunnels.some((t) => t?.id === tunnelId)) {
                const stub = { id: tunnelId, name: tunnelId, peerPublicIp: '', peerCidr: '' };
                tunnels.unshift({ ...stub, tunnelSelectLabel: this.formatTunnelOptionLabel(stub) });
            }
            this.ipsecTunnelList = tunnels;
        }
    }

    /**
     * Where a client behind the customer's device reaches this share.
     *
     * The address belongs to the tunnel, not the share — every share on a tunnel is published as a
     * port on the bridge's single private address — so it is resolved from the selected tunnel in
     * the already-loaded list rather than stored on the share. Returns null until both the tunnel
     * list has loaded and a port has been assigned, which the template renders as a dash.
     */
    get accessAddress(): string | null {
        const port = this.formData?.ingressPort;
        const tunnelId = this.formData?.ipsecTunnelId;
        if (!port || !tunnelId) {
            return null;
        }
        const tunnel = (this.ipsecTunnelList ?? []).find((t) => t?.id === tunnelId);
        return tunnel?.privateIp ? `${tunnel.privateIp}:${port}` : null;
    }

    getIpsecTunnels() {
        this.ipsecTunnelService.getIpsecTunnels(this.frontdoorId).then((data: any) => {
            const list = data?._embedded?.ipsecTunnelList ?? [];
            this.ipsecTunnelList = list.map((t) => ({
                ...t,
                tunnelSelectLabel: this.formatTunnelOptionLabel(t),
            }));
            this.ensureSelectOptionsPatchedForCurrentShare();
        });
    }

    private formatTunnelOptionLabel(t: { id: string; name?: string; peerPublicIp?: string; peerCidr?: string }): string {
        const label = t.name || t.id;
        if (t.peerPublicIp) {
            return `${label} — ${t.peerPublicIp}${t.peerCidr ? ` (${t.peerCidr})` : ''}`;
        }
        return label;
    }

    save(event?: any) {
        this.clearErrors();
        this.processing = true;
        this.isLoading = true;

        const model: any = {
            name: this.formData.name,
            ipsecTunnelId: this.formData.ipsecTunnelId,
            envZId: this.formData.envZId,
            target: this.formData.target,
            ingressPort: this.formData.ingressPort,
        };

        const httpCall = this.isEditing
            ? this.svc.update(model, this.frontdoorId, this.formData.id)
            : this.svc.create(model, this.frontdoorId);

        httpCall.subscribe({
            next: (result: any) => {
                if (this.isModal) {
                    this.isLoading = false;
                    this.processing = false;
                    this.closeModal(true, true);
                } else {
                    this.growlerService.show(
                        new GrowlerData(
                            'success',
                            'Success',
                            'IPsec share submitted',
                            'The share is being provisioned. It will appear as Deployed once complete.'
                        )
                    );
                    this.returnToListPage();
                }
            },
            error: (err) => {
                this.processErrors(err);
                this.isLoading = false;
                this.processing = false;
            },
        });
    }

    clearErrors() {
        this.nameError = false;
        this.nameErrorMessage = '';
        this.agentError = false;
        this.agentErrorMessage = '';
        this.targetError = false;
        this.targetErrorMessage = '';
        this.ipsecTunnelError = false;
        this.ipsecTunnelErrorMessage = '';
        this.ingressPortError = false;
        this.ingressPortErrorMessage = '';
    }

    loadMetrics() {
        this.metricsLoading = true;
        this.svc.getMetrics(this.frontdoorId, this.entityId).subscribe({
            next: (data: any) => {
                this.metricSamples = data?.samples ?? [];
                this.metricsLoading = false;
            },
            error: () => {
                this.metricSamples = [];
                this.metricsLoading = false;
            },
        });
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    clear(): void {
    }

    closeModal(refresh = true, ignoreChanges = false, data?: any, event?: any): void {
        this.close.emit({ refresh: refresh });
        if (event) {
            event.stopPropagation();
        }
    }

    closeForm(refresh = true, ignoreChanges = false, data?, event?) {
        if (!ignoreChanges && this._dataChange) {
            const confirmed = confirm('You have unsaved changes. Do you want to leave this page and discard your changes or stay on this page?');
            if (!confirmed) {
                return;
            }
        }
        if (this.isModal) {
            this.closeModal(refresh, ignoreChanges);
        } else {
            this.returnToListPage();
        }
    }

    returnToListPage() {
        if (this.location && this.previousRoute && this.usePreviousLocation) {
            this.location.back();
        } else {
            this.router?.navigateByUrl(`${this.basePath}/ipsec-shares`);
        }
    }

    processErrors(errorResponse: any) {
        const errors: string[] = errorResponse?.error?.errors || [];
        const envZIdError = errors.find(e => e.includes('envZId'));
        const targetError = errors.find(e => e.includes('target'));
        const nameError = errors.find(e => e.includes('name'));
        const ipsecTunnelError = errors.find(e => e.includes('ipsecTunnelId'));
        const ingressPortError = errors.find(e => e.includes('ingressPort'));

        if (envZIdError) {
            this.agentError = true;
            this.agentErrorMessage = envZIdError.split(':').slice(1).join(':').trim();
        }
        if (targetError) {
            this.targetError = true;
            this.targetErrorMessage = targetError.split(':').slice(1).join(':').trim();
        }
        if (nameError) {
            this.nameError = true;
            this.nameErrorMessage = nameError;
        }
        if (ipsecTunnelError) {
            this.ipsecTunnelError = true;
            this.ipsecTunnelErrorMessage = ipsecTunnelError.split(':').slice(1).join(':').trim();
        }
        if (ingressPortError) {
            this.ingressPortError = true;
            this.ingressPortErrorMessage = ingressPortError.split(':').slice(1).join(':').trim();
        }

        this.growlerService.show(
            new GrowlerData('error', 'Error', 'IPsec share request failed', 'The request has failed.')
        );
    }


    /**
     * True when this form is showing an existing entity. Derived from the route/input
     * rather than the loaded record, so the header title doesn't briefly read
     * "Create New ..." while the record is still being fetched.
     */
    get isExistingEntity(): boolean {
        return !isEmpty(this.formData?.id) || (!isEmpty(this.entityId) && this.entityId !== 'create');
    }

}
